import { css } from '@emotion/react';
import { RefundModel } from '@frontend/api-refunds';
import { Chips } from '@frontend/chips';
import { formatDate } from '@frontend/date';
import { CardBrand } from '@frontend/payments-card-brand';
import { TableColumnConfig } from '@frontend/design-system';
import { paymentTypeMap } from '../../../hooks/use-payment-type-map';
import { formatCentsToCurrency } from '../../../utils/currency-helpers';
import { getPaymentMethodBrand, GetLocationName } from '../../../utils/helpers';
import { PaymentsListContactName } from '../../PaymentsContactName/payments-list-contact-name';
import { RefundStatusChip } from '../RefundStatusChip';

export const generateColumns = (
  getLocationName: GetLocationName,
  showLocationName: boolean,
  forPrint = false
): TableColumnConfig<RefundModel>[] => [
  {
    id: 'location',
    Header: 'Location Name',
    accessor: (refund: RefundModel) => refund.locationId,
    cellRenderer: (locationId: string) =>
      forPrint ? (
        getLocationName(locationId ?? '')
      ) : (
        <Chips.LocationChip
          css={css`
            max-width: none;
          `}
        >
          {getLocationName(locationId ?? '')}
        </Chips.LocationChip>
      ),
    disableSortBy: true,
    omit: !showLocationName,
  },
  {
    id: 'person',
    Header: 'Contact Name',
    minWidth: 150,
    maxWidth: 170,
    disableSortBy: true,
    accessor: (refund) => refund,
    cellRenderer: (refund: RefundModel) => (
      <PaymentsListContactName id={refund.person.id} name={refund.person.name} locationId={refund.locationId} />
    ),
  },
  {
    id: 'patientId',
    Header: 'Patient Id',
    accessor: (refund) => refund?.person?.id,
    minWidth: 90,
    width: 90,
    disableSortBy: true,
  },
  {
    id: 'refundType',
    Header: 'Refund Type',
    accessor: (refund) => <RefundStatusChip refund={refund} />,
    minWidth: 64,
    maxWidth: 120,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    id: 'refundAmount',
    Header: 'Refund Amount',
    accessor: (refund) => (refund?.refundAmount ? formatCentsToCurrency(refund.refundAmount) : ''),
    minWidth: 120,
    maxWidth: 150,
    disableResizing: true,
    disableSortBy: true,
    cellAlign: 'right',
  },
  {
    id: 'dateRefunded',
    Header: 'Refund Date',
    accessor: (refund) => (refund?.dateRefunded ? formatDate(refund.dateRefunded, 'MMM D, YYYY') : ''),
    disableResizing: true,
    minWidth: 120,
    maxWidth: 150,
    disableSortBy: true,
  },
  {
    id: 'paymentType',
    Header: 'Payment Type',
    accessor: (refund) => (refund?.payment ? paymentTypeMap[refund.payment.origin] : ''),
    minWidth: 64,
    maxWidth: 120,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    id: 'paymentMethod',
    Header: 'Payment Method',
    accessor: (refund) => refund,
    cellRenderer: (refund: RefundModel) => {
      const last4 = refund.payment?.last4;
      return (
        <CardBrand
          brand={getPaymentMethodBrand({
            paymentType: refund.payment?.paymentType,
            brand: refund.payment?.brand,
            bankName: refund.payment?.bankName,
          })}
          last4={last4}
        />
      );
    },
    maxWidth: 180,
    disableSortBy: true,
  },
  {
    id: 'paidAmount',
    Header: 'Payment Amount',
    accessor: (refund) => (refund?.payment?.amount ? formatCentsToCurrency(+refund.payment?.amount) : ''),
    minWidth: 120,
    maxWidth: 150,
    disableResizing: true,
    disableSortBy: true,
    cellAlign: 'right',
  },
  {
    id: 'paidAt',
    Header: 'Payment Date',
    maxWidth: 150,
    accessor: (refund) => (refund?.payment?.date ? formatDate(refund.payment?.date, 'MMM D, YYYY') : ''),
    disableResizing: true,
    disableSortBy: true,
  },
  {
    id: 'paymentId',
    Header: 'Payment ID',
    accessor: (refund) => refund?.payment?.id || '',
    minWidth: 90,
    width: 90,
    disableSortBy: true,
  },
  {
    id: 'refundId',
    Header: 'Refund ID',
    minWidth: 90,
    width: 90,
    accessor: (refund) => refund?.id,
    disableSortBy: true,
  },
];
